import {useState} from 'react'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {Helmet} from 'react-helmet'
import {Navigate} from 'react-router-dom'
import {useTranslation} from '@/translations/i18n'
import {AuthForm, AuthHeading, AuthWrapper} from '@/components/ui/auth-atoms/AuthAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {TextArea} from '@/components/commons/textarea/TextArea'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {DetailsPageWrapper} from '@/components/ui/details-atoms/DetailsAtoms'
import {AlertCircleIcon, CheckCircleIcon} from '@/components/ui/icon/Icon'
import {palette} from '@/theme/palette'
import {routes} from '@/utilities/constants/routes'
import {useAuthStore} from '@/features/auth/store/store'
import {useAccountDeletionToken} from '@/features/account-deletion/hooks/useAccountDeletionToken'
import {useDeletionAccountUser} from '@/features/account-deletion/services/useDeletionAccountUser'
import {useRequestAccountDeletion} from '@/features/account-deletion/services/useRequestAccountDeletion'

const RequestAccountDeletionValidationSchema = z.object({
    note: z.string().optional(),
    ownershipConfirmed: z.boolean().refine(value => value === true)
})
type RequestAccountDeletionValidationSchema = z.infer<typeof RequestAccountDeletionValidationSchema>

/**
 * Public page opened by the mobile app inside a WebView to request account
 * deletion.
 *
 * Token sources, in order of precedence:
 *  1. the access token passed in the URL hash by the mobile WebView;
 *  2. the access token of an already logged-in web session.
 *
 * When neither is available the user is sent to the login page with a
 * `redirect` back to this page, so after logging in they land here
 * automatically (see PublicRoute).
 */
export const RequestAccountDeletion = () => {
    const {token: hashToken, isResolved} = useAccountDeletionToken()
    const sessionToken = useAuthStore(state => state.accessToken)

    // Momentary: useLayoutEffect resolves the hash token before the browser paints.
    if (!isResolved) return null

    const effectiveToken = hashToken ?? sessionToken ?? null

    if (!effectiveToken) {
        const redirect = encodeURIComponent(routes.REQUEST_ACCOUNT_DELETION.path)
        return <Navigate to={`${routes.LOGIN.path}?redirect=${redirect}`} replace />
    }

    return <RequestAccountDeletionForm token={effectiveToken} />
}

RequestAccountDeletion.displayName = 'RequestAccountDeletion'

const RequestAccountDeletionForm = ({token}: {token: string}) => {
    const {t} = useTranslation()
    const {data: user, isLoading, isError} = useDeletionAccountUser(token)
    const [isSubmitError, setIsSubmitError] = useState(false)

    const {mutate, isPending, isSuccess} = useRequestAccountDeletion({
        onError: () => setIsSubmitError(true)
    })

    const form = useForm<RequestAccountDeletionValidationSchema>({
        mode: 'onChange',
        resolver: zodResolver(RequestAccountDeletionValidationSchema),
        defaultValues: {note: '', ownershipConfirmed: false}
    })

    const ownershipConfirmed = form.watch('ownershipConfirmed')

    if (isLoading) {
        return (
            <AuthWrapper>
                <Spinner size={32} />
            </AuthWrapper>
        )
    }

    if (isError || !user) {
        return (
            <DetailsPageWrapper isSuccess={false}>
                <ErrorBox icon={<AlertCircleIcon />} title={t('request_account_deletion:identity_error_title')} />
            </DetailsPageWrapper>
        )
    }

    if (isSuccess) {
        return (
            <AuthWrapper>
                <Helmet title={t('request_account_deletion:meta_title')} />
                <Flexbox direction="column" gap={6} align="center">
                    <CheckCircleIcon size={48} fill={palette.light.success[600]} />
                    <AuthHeading
                        title={t('request_account_deletion:success_title')}
                        subtitle={t('request_account_deletion:success_message')}
                    />
                </Flexbox>
            </AuthWrapper>
        )
    }

    const handleSubmit = form.handleSubmit(values => {
        setIsSubmitError(false)
        mutate({name: user.fullName, email: user.email, note: values.note})
    })

    return (
        <AuthWrapper>
            <Helmet title={t('request_account_deletion:meta_title')} />
            <AuthForm fullWidth align="center" justify="center" gap={8} onSubmit={handleSubmit}>
                <AuthHeading
                    showLogo
                    title={t('request_account_deletion:title')}
                    subtitle={t('request_account_deletion:subtitle')}
                />
                <Flexbox direction="column" gap={4} fullWidth>
                    <InputText
                        type="text"
                        label={t('request_account_deletion:name_label')}
                        value={user.fullName}
                        readOnly
                        disabled
                    />
                    <InputText
                        type="email"
                        label={t('request_account_deletion:email_label')}
                        value={user.email}
                        readOnly
                        disabled
                    />
                    <TextArea
                        label={t('request_account_deletion:note_label')}
                        placeholder={t('request_account_deletion:note_placeholder')}
                        isOptional
                        {...form.register('note')}
                    />
                    <Checkbox
                        id="ownershipConfirmed"
                        label={t('request_account_deletion:ownership_confirmation_label')}
                        {...form.register('ownershipConfirmed')}
                    />
                </Flexbox>
                <Button type="submit" fullWidth variant="primaryNeutral" disabled={!ownershipConfirmed || isPending}>
                    {t('request_account_deletion:submit')}
                    {isPending && <Spinner size={16} />}
                </Button>
                {isSubmitError && <InputErrorText>{t('request_account_deletion:submit_error')}</InputErrorText>}
            </AuthForm>
        </AuthWrapper>
    )
}

RequestAccountDeletionForm.displayName = 'RequestAccountDeletionForm'

const InputErrorText = ({children}: {children: string}) => (
    <Flexbox align="center" gap={1.5} justify="center">
        <AlertCircleIcon size={16} />
        <span>{children}</span>
    </Flexbox>
)
